// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useCallback, useMemo } from 'react';
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import {
  getWorkOrder,
  getWorkOrderSummary,
  listWorkOrders,
  type WorkOrderSite,
} from '../services/workOrderService';
import type { WorkOrder, WorkOrderListParams, WorkOrderSummary } from '../types';

/**
 * Work orders are executed by an outside provider, so they change on the
 * provider's schedule rather than ours. A slower poll than the alarm surfaces
 * is deliberate: nothing here is real-time, and every poll costs an upstream
 * call against a rate-limited third-party API.
 */
const POLL_INTERVAL_MS = 120_000;

interface UseWorkOrdersResult {
  /** Every page loaded so far, in upstream order. */
  workOrders: WorkOrder[];
  /** The upstream has a page after the last one loaded. */
  hasNext: boolean;
  loading: boolean;
  /** True for the first load and every background poll; lets a screen show a subtle refresh hint without blanking the list. */
  isFetching: boolean;
  error: string | null;
  /**
   * Why the last background poll failed while an earlier load is still shown.
   * Null when there is nothing to show (that is `error`) or the last poll
   * succeeded. The list on screen is from the last successful load.
   */
  refreshError: string | null;
  refetch: () => void;
  /** Appends the next page. A no-op while one is already loading or none is left. */
  loadMore: () => void;
  loadingMore: boolean;
  /** Why the last "load more" failed; the pages already loaded stay. */
  loadMoreError: string | null;
}

interface UseWorkOrderSummaryResult {
  summary: WorkOrderSummary | null;
  loading: boolean;
  error: string | null;
  /** As on UseWorkOrdersResult: the summary shown is from the last successful poll. */
  refreshError: string | null;
  refetch: () => void;
}

/**
 * The list as cursor-paged infinite query: each "load more" appends the page
 * after `next_cursor`. Under the 'work-orders' prefix so a mutation's
 * invalidation refetches every loaded page. Keyed by site: a null site is the
 * whole fleet.
 */
export function workOrdersQueryOptions(
  siteId: WorkOrderSite,
  params: Omit<WorkOrderListParams, 'cursor'>
) {
  return infiniteQueryOptions({
    queryKey: [
      'work-orders',
      siteId,
      params.status ?? null,
      params.title ?? null,
      params.provider ?? null,
      params.page_size ?? null,
      params.sort ?? null,
    ] as const,
    queryFn: ({ pageParam }) => listWorkOrders(siteId, { ...params, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: last =>
      last.context?.has_next && last.context.next_cursor ? last.context.next_cursor : undefined,
  });
}

export function workOrderSummaryQueryOptions(siteId: WorkOrderSite) {
  return queryOptions({
    queryKey: ['work-order-summary', siteId] as const,
    queryFn: () => getWorkOrderSummary(siteId),
  });
}

const NO_WORK_ORDERS: WorkOrder[] = [];

function messageOf(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : String(error);
}

export function useWorkOrders(
  siteId: WorkOrderSite,
  params: Omit<WorkOrderListParams, 'cursor'> = {},
  enabled = true
): UseWorkOrdersResult {
  // ponytail: the poll refetches every loaded page in turn (react-query's
  // infinite refetch), so N pages cost N upstream calls per poll. Cap the
  // pages refetched if operators routinely load many.
  const query = useInfiniteQuery({
    ...workOrdersQueryOptions(siteId, params),
    enabled,
    refetchInterval: POLL_INTERVAL_MS,
  });

  const { refetch: refetchQuery, fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  const refetch = useCallback(() => {
    refetchQuery();
  }, [refetchQuery]);
  // Not during any fetch: fetchNextPage cancels an in-flight refetch (a poll,
  // or a mutation's invalidation) and appends to the stale pages, so a status
  // just changed could stay stale until the next poll.
  const { isFetching } = query;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetching) fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetching]);

  const workOrders = useMemo(
    () => query.data?.pages.flatMap(page => page.data) ?? NO_WORK_ORDERS,
    [query.data]
  );

  return {
    workOrders,
    hasNext: hasNextPage,
    loading: enabled && query.isLoading,
    // A "load more" is reported on its own, not as a background refresh.
    isFetching: enabled && query.isFetching && !isFetchingNextPage,
    // Keep the last good list visible through a transient poll failure; only
    // report `error` when there is nothing to show — but say the list is
    // stale, or it reads as current.
    error: query.data ? null : messageOf(query.error),
    refreshError:
      query.data && query.isError && !query.isFetchNextPageError ? messageOf(query.error) : null,
    refetch,
    loadMore,
    loadingMore: isFetchingNextPage,
    loadMoreError: query.isFetchNextPageError ? messageOf(query.error) : null,
  };
}

export function useWorkOrderSummary(
  siteId: WorkOrderSite,
  enabled = true
): UseWorkOrderSummaryResult {
  const query = useQuery({
    ...workOrderSummaryQueryOptions(siteId),
    enabled,
    refetchInterval: POLL_INTERVAL_MS,
  });

  return {
    summary: query.data ?? null,
    loading: enabled && query.isLoading,
    error: query.data ? null : messageOf(query.error),
    refreshError: query.data && query.isError ? messageOf(query.error) : null,
    refetch: () => {
      query.refetch();
    },
  };
}

/**
 * One work order by id, for a link to an order the current list page doesn't
 * hold (older than the page, or outside the filter). Under the 'work-orders'
 * prefix so the status mutation's invalidation refreshes it too.
 *
 * `workOrder` is null while disabled or loading too, so `unavailable` is what
 * tells a finished lookup apart: the id is unknown upstream (deleted order,
 * bad link) or the read failed.
 */
export function useWorkOrderLookup(
  siteId: WorkOrderSite,
  workOrderId: string | null,
  enabled = true
): { workOrder: WorkOrder | null; unavailable: boolean } {
  const query = useQuery({
    queryKey: ['work-orders', 'by-id', siteId, workOrderId] as const,
    queryFn: () => getWorkOrder(siteId, workOrderId as string),
    enabled: enabled && !!workOrderId,
  });
  return {
    workOrder: query.data ?? null,
    unavailable: query.isError || (query.isSuccess && !query.data),
  };
}
